import { computed, inject, InjectionToken, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { CompanySwitchStrategy } from '@core/http-legacy';
import { CompanyVerificationDataService } from '@core/tellent-company-verification-logic';
import { CompanyVerificationRequestStatus } from '@core/tellent-company-verification-types';
// eslint-disable-next-line boundaries/element-types
import { TellentCurrentUserSelectors } from '@core/tellent-data';
import { Org, Seat, TellentCurrentUser, TellentMember, UserProfileTab } from '@core/tellent-types';

type TellentProfileDropdownConfig = {
  companies: Signal<TellentMember[]>;
  currentUser: Signal<TellentCurrentUser>;
  currentMember: Signal<TellentMember | undefined>;
  currentOrg: Signal<Org | undefined>;
  currentSeat: Signal<Seat | undefined>;
  companyVerificationStatus: Signal<CompanyVerificationRequestStatus | undefined>;
  handlers: {
    switchCompany: (orgGuid: string) => void;
    editProfile: (analyticsContext?: string) => void;
    manageCompanies: (analyticsContext?: string) => void;
  };
};

type TellentProfileDropdownConfigFactory = () => TellentProfileDropdownConfig;

export const TellentProfileDropdownConfigToken = new InjectionToken<TellentProfileDropdownConfig>(
  'TellentProfileDropdownConfig',
);

export const provideTellentProfileDropdownConfig = (
  factory: TellentProfileDropdownConfigFactory,
) => ({
  provide: TellentProfileDropdownConfigToken,
  useFactory: factory,
});

export const injectTellentProfileDropdownConfig = () => inject(TellentProfileDropdownConfigToken);

export const provideTellentProfileDropdownConfigFactory = () =>
  provideTellentProfileDropdownConfig(() => {
    const router = inject(Router);
    const store = inject(Store);
    const switchStrategy = inject(CompanySwitchStrategy);
    const companyVerificationDataService = inject(CompanyVerificationDataService);
    const currentMember = store.selectSignal(
      TellentCurrentUserSelectors.currentOrganizationMembershipSelector,
    );
    const currentSeat = computed(() => currentMember().seats[0]!);
    const currentUser = <Signal<TellentCurrentUser>>(
      store.selectSignal(TellentCurrentUserSelectors.data)
    );
    const companies = computed(() => currentUser()!.members);

    const currentOrg = computed(() => {
      const user = currentUser();
      return user.members.find(m => m.org.guid === user.currentOrgGuid)?.org;
    });

    const companyVerificationStatus = toSignal(companyVerificationDataService.status$);

    return {
      companies,
      currentUser,
      currentMember,
      currentSeat,
      currentOrg,
      companyVerificationStatus,
      handlers: {
        switchCompany: orgGuid => switchStrategy.switchCompany(orgGuid),
        editProfile: (analyticsContext?: string) => {
          router.navigate([{ outlets: { dialog: 'edit-profile' } }], {
            queryParams: { viewContext: analyticsContext },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        },
        manageCompanies: (analyticsContext?: string) => {
          router.navigate([{ outlets: { dialog: 'edit-profile' } }], {
            queryParams: { tab: UserProfileTab.Companies, viewContext: analyticsContext },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        },
      },
    };
  });
